/**
 * Client state: one reducer over server messages.
 *
 * The snapshot is the truth and events are the narration. State never tries to
 * advance the game itself — it holds the last snapshot, a short action feed
 * derived from events, and connection bookkeeping. The moment the client
 * starts simulating ("a call means the pot must now be..."), it can disagree
 * with the server, and a table that argues with its server is unplayable.
 */

import type {
  AccountView,
  ServerHandEvent,
  ServerMessage,
  TableListing,
  TableSnapshot,
  TournamentListing,
} from '@pf/protocol';

export interface SeatAction {
  readonly kind: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'blind';
  /** Chips this seat has in front of it after the action, or 0. */
  readonly to: number;
  readonly allIn: boolean;
  /** Changes on every action, so the badge re-animates rather than sitting there. */
  readonly id: number;
}

export interface FeedItem {
  readonly id: number;
  readonly text: string;
  /** Marks the rows worth emphasising: wins and showdowns. */
  readonly strong: boolean;
}

export interface ClientState {
  /** Null until the connection has an account. The login screen watches this. */
  readonly account: AccountView | null;
  readonly tables: readonly TableListing[];
  readonly tournaments: readonly TournamentListing[];
  /** Kept so feed lines can be written in big blinds like everything else. */
  readonly bigBlind: number;
  readonly snapshot: TableSnapshot | null;
  readonly feed: readonly FeedItem[];
  readonly lastError: { code: string; message: string } | null;
  /** The last thing that went right and is worth saying — a sent link, a reset. */
  readonly lastNotice: { code: string; message: string } | null;
  /** Bumped on every hand-complete, so effects can react to hand boundaries. */
  readonly handsCompleted: number;
  /**
   * What each seat last did on this street.
   *
   * The side panel is a hand history; this is the table. A player watching a
   * six-handed pot should not have to read a list to find out that the seat
   * two to their left just raised — the verb belongs on the person who said
   * it, which is where it is in every card room and every good client.
   *
   * Cleared when the street turns over, because "called" stops being news the
   * moment new cards are on the felt.
   */
  readonly lastActions: ReadonlyMap<number, SeatAction>;
}

export const initialState: ClientState = {
  account: null,
  tables: [],
  tournaments: [],
  bigBlind: 1,
  snapshot: null,
  feed: [],
  lastError: null,
  lastNotice: null,
  handsCompleted: 0,
  lastActions: new Map(),
};

let feedId = 0;

function push(feed: readonly FeedItem[], text: string, strong = false): readonly FeedItem[] {
  const next = [...feed, { id: ++feedId, text, strong }];
  // The feed is a glance at recent action, not a hand history archive — the
  // real history is the event log on the server.
  return next.length > 60 ? next.slice(next.length - 60) : next;
}

/**
 * Things the client tells itself.
 *
 * Everything else in this reducer comes off the wire. These do not: leaving a
 * table is a decision about what is on screen, and waiting for the server to
 * confirm it would leave the table up for a round trip after the player asked
 * to go.
 */
export type LocalAction = { readonly type: 'left-table' };

export function reduce(state: ClientState, message: ServerMessage | LocalAction): ClientState {
  switch (message.type) {
    case 'welcome':
      return {
        ...state,
        account: message.account,
        tables: message.tables,
        tournaments: message.tournaments,
        lastError: null,
      };

    case 'tournaments':
      return { ...state, tournaments: message.tournaments };

    case 'session':
      return { ...state, account: message.account, lastError: null };

    case 'notice':
      // A notice clears the error beside it: the two are answers to the same
      // question, and showing yesterday's failure under today's success reads
      // as though something is still wrong.
      return { ...state, lastNotice: { code: message.code, message: message.message }, lastError: null };

    case 'balance':
      // The wallet moved. Nothing else about the account changed, and rebuilding
      // it from scratch would drop the fields this message does not carry.
      return state.account === null
        ? state
        : {
            ...state,
            account: {
              ...state.account,
              balance: message.balance,
              dailyGrantAvailable: message.dailyGrantAvailable,
            },
          };

    case 'snapshot':
      return { ...state, snapshot: message.table, bigBlind: message.table.bigBlind };

    // Local, and not from the server: the lobby is a client-side state, and
    // waiting for the socket to confirm it would leave the table on screen for
    // a round trip after the player asked to leave.
    case 'left-table':
      return { ...state, snapshot: null, feed: [], lastActions: new Map() };

    case 'event':
      return applyEvent(state, message.event);

    case 'error':
      // Rate limiting and stale-hand rejections are normal play, not alerts.
      if (message.code === 'stale-hand') return state;
      return {
        ...state,
        lastError: { code: message.code, message: message.message },
        lastNotice: null,
      };

    case 'seat-update':
    case 'pong':
      return state;
  }
}

/**
 * Record what a seat just did.
 *
 * A fresh id every time, so a player who calls twice on the same street gets
 * the badge to flash again rather than sit there looking stale.
 */
let actionId = 0;
function withAction(
  actions: ReadonlyMap<number, SeatAction>,
  seat: number,
  action: Omit<SeatAction, 'id'>,
): ReadonlyMap<number, SeatAction> {
  const next = new Map(actions);
  next.set(seat, { ...action, id: ++actionId });
  return next;
}

function seatName(state: ClientState, seat: number): string {
  return state.snapshot?.seats.find((s) => s.seat === seat)?.name ?? `صندلی ${seat + 1}`;
}

import { ACTION_LABEL, STREET_LABEL, bb } from './fa.ts';

/**
 * The feed is a hand history in miniature, and hand histories are written in
 * the language of the game. Amounts are big blinds for the same reason they are
 * everywhere else: '3BB' says something about the spot, '6' does not.
 */
function applyEvent(state: ClientState, event: ServerHandEvent): ClientState {
  switch (event.type) {
    case 'hand-started':
      return {
        ...state,
        feed: push(state.feed, `— Hand #${event.handNumber} —`),
        lastActions: new Map(),
      };

    case 'ante-posted':
      return {
        ...state,
        feed: push(state.feed, `${seatName(state, event.seat)} — Ante ${bb(event.amount, state.bigBlind)}`),
      };

    case 'blind-posted':
      return {
        ...state,
        feed: push(
          state.feed,
          `${seatName(state, event.seat)} — ${event.blind === 'small' ? 'SB' : 'BB'} ${bb(event.amount, state.bigBlind)}`,
        ),
        lastActions: withAction(state.lastActions, event.seat, {
          kind: 'blind',
          to: event.amount,
          allIn: event.allIn,
        }),
      };

    case 'acted': {
      const verb = event.allIn ? 'All In' : (ACTION_LABEL[event.action] ?? event.action);
      const amount =
        event.action === 'fold' || event.action === 'check' ? '' : ` ${bb(event.to, state.bigBlind)}`;
      const suffix = event.byTimeout ? ' (timeout)' : '';
      return {
        ...state,
        feed: push(state.feed, `${seatName(state, event.seat)} — ${verb}${amount}${suffix}`),
        lastActions: withAction(state.lastActions, event.seat, {
          kind: event.action,
          to: event.to,
          allIn: event.allIn,
        }),
      };
    }

    case 'street-dealt':
      return {
        ...state,
        feed: push(
          state.feed,
          `${STREET_LABEL[event.street] ?? event.street}   ${event.cards.join('  ')}`,
        ),
        // "Called" stops being news the moment new cards are on the felt.
        lastActions: new Map(),
      };

    case 'raked':
      return {
        ...state,
        feed: push(state.feed, `Rake ${bb(event.total, state.bigBlind)}`),
      };

    case 'uncalled-returned':
      return {
        ...state,
        feed: push(
          state.feed,
          `${seatName(state, event.seat)} — uncalled ${bb(event.amount, state.bigBlind)} returned`,
        ),
      };

    case 'showdown':
      return {
        ...state,
        feed: push(
          state.feed,
          `SHOWDOWN   ${event.shown.map((s) => `${seatName(state, s.seat)} ${s.cards.join(' ')}`).join('   ·   ')}`,
          true,
        ),
      };

    case 'awarded':
      return {
        ...state,
        feed: push(
          state.feed,
          `${seatName(state, event.seat)} wins +${bb(event.amount, state.bigBlind)}`,
          true,
        ),
      };

    case 'hand-complete':
      return { ...state, handsCompleted: state.handsCompleted + 1 };

    case 'hole-cards-dealt':
    case 'pots-built':
      return state;
  }
}
